<template>
  <template v-if="clientSideOnly">
    <client-only fallback="...">
      <component
        :is="inputComponent"
        :auxInputType="props.auxInputType"
        v-model:value="value"
        v-model:form="form"
        v-model:actual="actualValue"
        :id="props.id"
        :name="props.name"
        :error="props.errors"
        :accepts="props.accepts"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        @input="inputEventHandler($event)"
        @blur="blurEventHandler($event)"
        @multiSelectAllChange="multiSelectAllChangeEventHandler($event)"
        @change="changeEventHandler($event)"
        @enter="
          ($event: any) => {
            emits('onEnterPressed', $event);
          }
        "
        v-bind="{ ...$attrs, ...bindings }"
        :dataKey="props.dataKey"
        :title="props.placeholder"
        :affectFields="props.affectFields"
        :showAllOptionInSelect="props.showAllOptionInSelect"
        :min="props.min"
        :max="props.max"
        :required="props.required"
        :context="props.context"
        :searchType="props.searchType"
      />
    </client-only>
  </template>
  <template v-else>
    <component
      :is="inputComponent"
      :auxInputType="props.auxInputType"
      v-model:value="value"
      v-model:form="form"
      v-model:actual="actualValue"
      :id="props.id"
      :name="props.name"
      :accepts="props.accepts"
      :error="props.errors"
      :placeholder="props.placeholder"
      :disabled="props.disabled"
      @change="changeEventHandler($event)"
      @input="inputEventHandler($event)"
      @blur="blurEventHandler($event)"
      @enter="
        ($event: any) => {
          emits('onEnterPressed', $event);
        }
      "
      v-bind="{ ...$attrs, ...bindings }"
      :dataKey="props.dataKey"
      :title="props.placeholder"
      :affectFields="props.affectFields"
      :showAllOptionInSelect="props.showAllOptionInSelect"
      :min="props.min"
      :max="props.max"
      :required="props.required"
      :context="props.context"
      :searchType="props.searchType"
    />
  </template>
</template>

<script setup lang="ts">
import { InputType } from "~/scad-shared/types/generic";
import {
  type IInputEvent,
  type IChangeEvent,
  type IBlurEvent,
  type IMisc,
} from "~/scad-shared/types/interfaces";
import { getComponentMap } from "~/scad-shared/config";
import { defaultFallbackComponent } from "~/scad-shared/config/componentMap";
// Try to import component map from config in root folder

const clientSideOnly = ref<boolean>(true);

const props = withDefaults(
  defineProps<{
    value?: any;
    disabled?: boolean;
    inputType?: InputType;
    auxInputType?: string;
    searchType?: string;
    errors?: string;
    loading?: boolean;
    id?: string;
    accepts?: string;
    misc?: IMisc;
    placeholder?: string;
    name?: string;
    affectFields?: string[];
    dataKey: string;
    showAllOptionInSelect?: boolean;
    min?: any;
    max?: any;
    tableName?: string;
    required?: boolean;
    defaultExpression?: string;
    context?: Indexable;
  }>(),
  {
    loading: false,
    disabled: false,
    context: () => ({}),
    inputType: InputType.InputText,
  },
);

const bindings = ref<Indexable>({});

const inputComponent = computed(() => {
  const componentMap = getComponentMap();
  const config = componentMap[props.inputType];

  // Reset bindings for each computation
  bindings.value = {};

  if (config) {
    // Apply bindings configuration if defined
    if (config.configureBindings) {
      config.configureBindings(bindings.value, props);
    }

    // Handle clientSideOnly flag (defaults to true)
    if (config.clientSideOnly !== undefined) {
      clientSideOnly.value = config.clientSideOnly;
    } else {
      clientSideOnly.value = true;
    }

    return config.component;
  }

  // Default fallback to text input
  clientSideOnly.value = true;
  return defaultFallbackComponent.component;
});

// const value: Ref<any> = ref(props.value);

const value = defineModel<any>("value");
const form = defineModel<any>("form");
const actualValue = defineModel<any>("actual");

const emits = defineEmits<{
  change: [event: IInputEvent];
  input: [event: IChangeEvent];
  blur: [event: IBlurEvent];
  multiSelectAllChange: [event: boolean];
  onEnterPressed: [event: any];
}>();

const inputTypeLowerCase = computed(() => {
  return props.inputType.toLowerCase();
});

function changeEventHandler($event: IChangeEvent) {
  // console.log("changeEventHandler", $event);

  nextTick(() => {
    emits("change", { ...$event, ...{ value: value.value } });
  });
}

function inputEventHandler($event: IInputEvent) {
  emits("input", { ...$event, ...{ value: value.value } });
}

function blurEventHandler($event: IBlurEvent) {
  emits("blur", { ...$event, ...{ value: value.value } });
}

function multiSelectAllChangeEventHandler($event: boolean) {
  emits("multiSelectAllChange", $event);
}
</script>

<style scoped></style>
